import { useId, useRef } from 'react';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import './OtpInput.css';

export const OTP_CODE_LENGTH = 6;

export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '').slice(0, OTP_CODE_LENGTH);
}

export interface OtpInputProps {
  value: string;
  onValueChange: (value: string) => void;
  label?: string;
  invalid?: boolean;
  describedBy?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  testId?: string;
}

/** Six boxes, one hidden field — SMS autocomplete and paste stay one input. */
export function OtpInput({
  value,
  onValueChange,
  label,
  invalid = false,
  describedBy,
  disabled = false,
  autoFocus = false,
  testId,
}: OtpInputProps) {
  const generatedId = useId();
  const inputId = generatedId;
  const inputRef = useRef<HTMLInputElement>(null);
  const digits = digitsOnly(value);

  const classes = ['otp-input'];
  if (invalid) {
    classes.push('otp-input--invalid');
  }
  if (disabled) {
    classes.push('otp-input--disabled');
  }

  return (
    <div
      className={classes.join(' ')}
      onClick={() => {
        if (!disabled) {
          inputRef.current?.focus();
        }
      }}
      {...testIdAttr(testId)}
    >
      {label ? (
        <div className="otp-input__header">
          <Typography
            as="label"
            role="label/field"
            htmlFor={inputId}
            {...testIdAttr(testIdFor(testId, 'label'))}
          >
            {label}
          </Typography>
        </div>
      ) : null}

      <div className="otp-input__stage">
        <div className="otp-input__cells" aria-hidden="true">
          {Array.from({ length: OTP_CODE_LENGTH }, (_, index) => {
            const cellClasses = ['otp-input__cell'];
            if (digits[index]) {
              cellClasses.push('otp-input__cell--filled');
            }
            if (!disabled && index === digits.length) {
              cellClasses.push('otp-input__cell--active');
            }
            if (invalid) {
              cellClasses.push('otp-input__cell--invalid');
            }
            return (
              <Typography
                key={index}
                as="span"
                role="title/card"
                className={cellClasses.join(' ')}
              >
                {digits[index] || ''}
              </Typography>
            );
          })}
        </div>
        <input
          ref={inputRef}
          id={inputId}
          className="otp-input__field"
          value={digits}
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus={autoFocus}
          pattern="[0-9]*"
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          aria-label={label}
          {...testIdAttr(testIdFor(testId, 'control'))}
          onChange={(event) => onValueChange(digitsOnly(event.target.value))}
        />
      </div>
    </div>
  );
}
